import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  differenceInMonths,
  differenceInWeeks,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import {memo, useMemo, useRef} from 'react'

import {RoadmapZoomLevel} from '../../../api/view/contracts'
import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {dateStringFromISODate} from '../../../helpers/date-string-from-iso-string'
import {intervalDateRange} from '../../../helpers/iterations'
import {getIterationOptionsTooltipText} from '../../../helpers/roadmap-helpers'
import {useRoadmapZoomLevel} from '../../../hooks/use-roadmap-settings'
import {
  isCurrentIterationByMarker,
  type RoadmapMarker,
  RoadmapMarkerType,
  useRoadmapMarkersInRange,
} from '../components/roadmap-markers-in-range'
import {startOfDayUtc} from '../date-utils'
import {useRoadmapView} from '../roadmap-view-provider'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-marker.module.css'

/**
 * Renders the small circles (or other shapes) at the top of a marker, at the bottom edge of the header
 * Currently includes the "Today" marker, but could be expanded to include other markers like "Iteration X".
 * */
export function RoadmapMarkerHeaderNubs() {
  return (
    <div role="row" className={styles.Box}>
      <TodayMarkerNub />
      <MarkerNubs />
    </div>
  )
}

/**
 * Renders the vertical marker lines within the roadmap item area
 * Currently includes the "Today" marker and roadmap date dividers, but could be expanded to include other markers like "Iteration X".
 * */
export function RoadmapMarkers() {
  return (
    <>
      <DividerMarkersLines />
      <TodayMarkerLine />
      <MarkerLines />
    </>
  )
}

function TodayMarkerNub() {
  const {getX, today} = useRoadmapView()
  const {singleDayMarkerMap} = useRoadmapMarkersInRange()

  const midday = useMemo(() => addHours(today, 12), [today])
  const leftOffset = getX(midday) - 3.5

  const ref = useRef<HTMLTimeElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `Today: ${getTooltipText({start: today, end: today})}`,
  })

  const todayDateString = dateStringFromISODate(today.toISOString())
  // if a single day marker is already present, don't render the today marker
  const isSingleDateMarkerPresent = singleDayMarkerMap.has(todayDateString)

  return (
    <time
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="gridcell"
      dateTime={todayDateString}
      ref={ref}
      className={clsx(
        styles.markerNubBase,
        isVisible && !isSingleDateMarkerPresent ? styles.todayNub : styles.invisibleNub,
      )}
      style={{left: `${leftOffset}px`}}
      {...contentProps}
      {...testIdProps(`roadmap-today-marker-nub`)}
    >
      {tooltip}
    </time>
  )
}

function TodayMarkerLine() {
  const {getX, today, totalWidth} = useRoadmapView()
  const {singleDayMarkerMap} = useRoadmapMarkersInRange()

  const midday = useMemo(() => addHours(today, 12), [today])
  const leftOffset = getX(midday)
  const isInTimeRange = leftOffset > 0 && leftOffset < totalWidth

  if (!isInTimeRange) return null

  const todayDateString = dateStringFromISODate(today.toISOString())
  // if a single day marker is already present, don't render the today marker
  if (singleDayMarkerMap.has(todayDateString)) return null

  return (
    <div
      // Slightly move marker to the left to accomodate thicker border on today marker
      style={{left: `${leftOffset - 0.5}px`, height: '100%'}}
      className={styles.Box_1}
      {...testIdProps(`roadmap-today-marker`)}
    />
  )
}
function MarkerNubs() {
  const {markersInRange} = useRoadmapMarkersInRange()

  return (
    <>
      {markersInRange.map(marker => (
        <MarkerNub marker={marker} key={marker.id} isCurrent={isCurrentIterationByMarker(marker)} />
      ))}
    </>
  )
}

const markerNubStyleMap = {
  [RoadmapMarkerType.CustomDate]: clsx(styles.markerNubBase, styles.customDateNub),
  [RoadmapMarkerType.Iteration]: clsx(styles.markerNubBase, styles.iterationNub),
  [RoadmapMarkerType.IterationBreak]: clsx(styles.markerNubBase, styles.iterationNub, styles.breakIterationNub),
  [RoadmapMarkerType.Milestone]: clsx(styles.markerNubBase, styles.milestoneNub),
}

function MarkerNub({isCurrent = false, marker}: {isCurrent?: boolean; marker: RoadmapMarker}) {
  const {getX, today} = useRoadmapView()

  const {startDate, endDate} = useMemo(() => {
    const markerStartDate = 'duration' in marker ? new Date(marker.date) : addHours(new Date(marker.date), 12)
    const markerEndDate = 'duration' in marker ? addDays(markerStartDate, marker.duration) : new Date(marker.date)
    return {
      startDate: markerStartDate,
      endDate: markerEndDate,
    }
  }, [marker])

  const leftOffset = getX(new Date(startDate)) - 3
  const ref = useRef<HTMLTimeElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const todayDateString = dateStringFromISODate(today.toISOString())
  // is today and single day marker
  const isToday = marker.date === todayDateString && !('duration' in marker)

  const {startDate: tooltipStart, endDate: tooltipEnd} =
    'duration' in marker
      ? intervalDateRange({
          startDate: marker.date,
          duration: marker.duration,
        })
      : {startDate, endDate}

  const tooltipText =
    'duration' in marker && marker.options.length > 1
      ? getIterationOptionsTooltipText(marker.options)
      : `${marker.title}: ${getTooltipText({
          start: tooltipStart,
          end: tooltipEnd,
        })}`

  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': tooltipText,
  })

  const className = isCurrent
    ? clsx(styles.markerNubBase, styles.iterationNub, styles.currentIterationNub)
    : markerNubStyleMap[marker.type]

  return (
    <time
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="gridcell"
      dateTime={marker.date}
      ref={ref}
      className={clsx(className, isToday && styles.markerNubToday)}
      style={{
        left: `${leftOffset}px`,
        ...(isVisible ? {} : {backgroundColor: 'transparent', borderColor: 'transparent'}),
      }}
      {...contentProps}
      {...testIdProps(`${marker.type}-marker-nub-${marker.id}`)}
    >
      {tooltip}
    </time>
  )
}

const MarkerLines = memo(function MarkerLines() {
  const {markersInRange} = useRoadmapMarkersInRange()

  return (
    <>
      {markersInRange.map(marker => (
        <MarkerLine key={marker.id} marker={marker} isCurrent={isCurrentIterationByMarker(marker)} />
      ))}
    </>
  )
})

const markerLineStyleMap = {
  [RoadmapMarkerType.CustomDate]: clsx(styles.markerLineBase, styles.customDateLine),
  [RoadmapMarkerType.Iteration]: clsx(styles.markerLineBase, styles.iterationLine),
  [RoadmapMarkerType.IterationBreak]: clsx(styles.markerLineBase, styles.iterationLine),
  [RoadmapMarkerType.Milestone]: clsx(styles.markerLineBase, styles.milestoneLine),
}

function MarkerLine({marker, isCurrent = false}: {marker: RoadmapMarker; isCurrent?: boolean}) {
  const {getX, today} = useRoadmapView()

  const date = useMemo(
    () => ('duration' in marker ? new Date(marker.date) : addHours(new Date(marker.date), 12)),
    [marker],
  )
  const leftOffset = getX(new Date(date))
  const todayDateString = dateStringFromISODate(today.toISOString())
  // is today and single day marker
  const isToday = marker.date === todayDateString && !('duration' in marker)
  const className = isCurrent
    ? clsx(styles.markerLineBase, styles.iterationLine, styles.currentIterationLine)
    : markerLineStyleMap[marker.type]

  return (
    <div
      {...testIdProps(`${marker.type}-marker-line`)}
      className={clsx(className, isToday && styles.markerLineToday)}
      style={{left: `${leftOffset}px`, height: `100%`}}
    />
  )
}

/** Renders subtle marker lines (no nubs) that are used to divide the roadmap area based on month/week starts */
function DividerMarkersLines() {
  const {getX, timeRangeEnd, timeRangeStart} = useRoadmapView()
  const zoomLevel = useRoadmapZoomLevel()

  const totalMarkers =
    zoomLevel === RoadmapZoomLevel.Month
      ? Math.abs(differenceInWeeks(timeRangeEnd, timeRangeStart)) + 1
      : Math.abs(differenceInMonths(timeRangeEnd, timeRangeStart)) + 1

  const dates = Array.from({length: totalMarkers}, (_, i) => {
    // Use Monday as the start of the week in the month view
    return zoomLevel === RoadmapZoomLevel.Month
      ? startOfDayUtc(addWeeks(startOfWeek(timeRangeStart, {weekStartsOn: 1}), i))
      : startOfDayUtc(addMonths(startOfMonth(timeRangeStart), i))
  })

  return (
    <>
      {dates.map(date => {
        const leftOffset = getX(date)
        return (
          <div
            key={leftOffset}
            style={{left: `${leftOffset}px`, height: `100%`}}
            className={clsx(styles.markerLineBase, styles.dividerLine)}
          />
        )
      })}
    </>
  )
}

try{ RoadmapMarkerHeaderNubs.displayName ||= 'RoadmapMarkerHeaderNubs' } catch {}
try{ RoadmapMarkers.displayName ||= 'RoadmapMarkers' } catch {}
try{ TodayMarkerNub.displayName ||= 'TodayMarkerNub' } catch {}
try{ TodayMarkerLine.displayName ||= 'TodayMarkerLine' } catch {}
try{ MarkerNubs.displayName ||= 'MarkerNubs' } catch {}
try{ MarkerNub.displayName ||= 'MarkerNub' } catch {}
try{ MarkerLines.displayName ||= 'MarkerLines' } catch {}
try{ MarkerLine.displayName ||= 'MarkerLine' } catch {}
try{ DividerMarkersLines.displayName ||= 'DividerMarkersLines' } catch {}